import { useState, useEffect } from "react";
import { Share2, Gift, Users, Copy, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { useToast } from "@/shared/hooks/use-toast";
import { apiClient } from "@/shared/services/api";

interface Referral {
  id: string;
  referee_email: string;
  status: string;
  created_at: string;
  conversion_value?: number;
}

export function ReferralSystem() {
  const [referralLink, setReferralLink] = useState("");
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [newReferralEmail, setNewReferralEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const { toast } = useToast();

  // Mock user email - in real app this would come from auth context
  const userEmail = "user@example.com";

  useEffect(() => {
    generateReferralLink();
    fetchReferrals();
  }, []);

  const generateReferralLink = async () => {
    try {
      setLoading(true);
      // SECURITY: Use backend proxy instead of calling edge function directly
      const data = await apiClient.post<{ success: boolean; data: { referral_link?: string } }>('/edge/rocket-referral', {
        action: 'get_referral_link',
        data: {
          referrer_email: userEmail,
          campaign_id: 'wlkthru-referral'
        }
      });

      if (data.success && data.data.referral_link) {
        setReferralLink(data.data.referral_link);
      }
    } catch (error) {
      console.error('Error generating referral link:', error);
      // Fallback to a demo link
      setReferralLink(`https://wlkthru.com/ref/${btoa(userEmail).slice(0, 8)}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    try {
      // SECURITY: Use backend proxy instead of calling edge function directly
      const data = await apiClient.post<{ success: boolean; data: { referrals?: Referral[] } }>('/edge/rocket-referral', {
        action: 'get_referrals',
        data: {
          email: userEmail
        }
      });

      if (data.success && data.data.referrals) {
        setReferrals(data.data.referrals);
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
      // Mock data for demo
      setReferrals([
        { id: '1', referee_email: 'john@example.com', status: 'pending', created_at: '2024-01-15' },
        { id: '2', referee_email: 'sarah@example.com', status: 'converted', created_at: '2024-01-10', conversion_value: 100 }
      ]);
    }
  };

  const createReferral = async () => {
    if (!newReferralEmail) {
      return;
    }

    setLoading(true);
    try {
      // SECURITY: Use backend proxy instead of calling edge function directly
      const data = await apiClient.post<{ success: boolean }>('/edge/rocket-referral', {
        action: 'create_referral',
        data: {
          referrer_email: userEmail,
          referee_email: newReferralEmail,
          campaign_id: 'wlkthru-referral'
        }
      });

      if (data.success) {
        toast({
          title: "Referral Created!",
          description: `Referral invitation sent to ${newReferralEmail}`,
        });
        setNewReferralEmail("");
        fetchReferrals();
      }
    } catch (error) {
      console.error('Error creating referral:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create referral",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      toast({
        title: "Link Copied!",
        description: "Referral link copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Could not copy link to clipboard",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'converted':
        return <Badge variant="default" className="bg-green-500">Converted</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Referral Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Referrals</p>
              <p className="text-2xl font-bold">{referrals.length}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div>
              <p className="text-sm text-muted-foreground">Converted</p>
              <p className="text-2xl font-bold">
                {referrals.filter(r => r.status === 'converted').length}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Earned</p>
              <p className="text-2xl font-bold">
                ${referrals.reduce((sum, r) => sum + (r.conversion_value || 0), 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Your Referral Link
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={referralLink}
              readOnly
              placeholder="Generating referral link..."
            />
            <Button 
              onClick={copyReferralLink}
              variant="outline"
              className="shrink-0"
            >
              {linkCopied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Share this link to earn rewards when someone signs up using your referral.
          </p>
        </CardContent>
      </Card>

      {/* Create New Referral */}
      <Card>
        <CardHeader>
          <CardTitle>Invite Someone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="referral-email">Email Address</Label>
            <div className="flex gap-2">
              <Input
                id="referral-email"
                type="email"
                placeholder="friend@example.com"
                value={newReferralEmail}
                onChange={(e) => setNewReferralEmail(e.target.value)}
              />
              <Button 
                onClick={createReferral}
                disabled={loading || !newReferralEmail}
              >
                {loading ? "Sending..." : "Send Invite"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card>
        <CardHeader>
          <CardTitle>Referral History</CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No referrals yet. Start by sharing your referral link!
            </p>
          ) : (
            <div className="space-y-3">
              {referrals.map((referral) => (
                <div key={referral.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{referral.referee_email}</p>
                    <p className="text-sm text-muted-foreground">
                      Referred on {new Date(referral.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {referral.conversion_value && (
                      <span className="text-sm font-medium text-green-600">
                        +${referral.conversion_value}
                      </span>
                    )}
                    {getStatusBadge(referral.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}